import { useNavigate, useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../../../api";
import {
  Input
} from "../../../components/ui/Input";
import CustomSelect from "../../../components/ui/CustomSelect";
import {
  Mail,
  Phone,
  User,
  Briefcase,
  Euro,
  Building2,
  FileText,
  UserCircle,
  Tag,
  Calendar,
  ClipboardList,
  CheckCircle2,
  Trash2,
  AlertTriangle,
  Check,
  Plus,
  ArrowLeft,
  Pencil,
  XCircle
} from "lucide-react";
import {
  Linkedin,
  Globe,
  Activity,
  MapPin,
  Eye,
  MessageSquare,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ProspectForm from "../../../features/prospection/components/ProspectForm";
import { useLoading } from "../../../context/LoadingContext";
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import { useNotification } from "../../../context/NotificationContext";
import { useModal } from "../../../context/ModalContext";
import MiniLoader from "../../../components/MiniLoader";
import NotFound from "../../errors/NotFound";
import Unauthorized from "../../errors/Unauthorized";
import Tooltip from "../../../components/ui/Tooltip";

export default function ProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoading } = useLoading();
  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [showAuditButton, setShowAuditButton] = useState(false);

  const { showToast } = useNotification();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFinalModal, _setShowFinalModal] = useState(false);

  // States for Notes d'équipe
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState("");

  const [interactions, setInteractions] = useState([]);
  const [prochainesActions, setProchainesActions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingInteractionId, setEditingInteractionId] = useState(null);
  const [formData, setFormData] = useState({
    typeInteraction: "",
    canal: "",
    date: "",
    contenu: "",
    prochaineEtape: "",
    dateProchaineInteraction: "",
    important: false,
  });

  const fetchProspect = async () => {
    try {
      const res = await api.get(`/prospects/${id}`);
      setProspect(res.data);
      setNotesValue(res.data.interestNotes || res.data.commentaire || "");
    } catch (error) {
      console.error("Erreur lors du chargement du prospect :", error);
      setErrorStatus(error.response?.status || 500);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProspect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (prospect?.status === "QUALIFIED" || prospect?.statut === "Prospect Qualifié") {
      setShowAuditButton(true);
    }
    if (prospect?._id || prospect?.id) {
      fetchInteractions();
      fetchNextSteps();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospect?.status, prospect?.statut, prospect?._id, prospect?.id]);

  async function fetchInteractions() {
    try {
      const res = await api.get(`/interactions/${prospect._id || prospect.id}`);
      const sorted = res.data.sort((a, b) => {
        if (b.important && !a.important) return 1;
        if (a.important && !b.important) return -1;
        return new Date(b.date || b.createdAt).getTime() - new Date(a.date || a.createdAt).getTime();
      });
      setInteractions(sorted);
    } catch (err) {
      console.error("Erreur récupération interactions :", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveInteraction = async () => {
    const { typeInteraction, canal, date, contenu } = formData;
    if (!typeInteraction || !canal || !date || !contenu) {
      showToast("Tous les champs obligatoires doivent être remplis.", "error");
      return;
    }

    try {
      if (editingInteractionId) {
        await api.put(`/interactions/${editingInteractionId}`, {
          ...formData,
        });
        showToast("Interaction modifiée avec succès", "success");
      } else {
        await api.post("/interactions", {
          ...formData,
          prospectId: prospect._id || prospect.id,
          referent: prospect.ownerMembership?.user?.displayName || prospect.ownerMembershipId || prospect.referent || "Non assigné",
          statut: prospect.status || prospect.statut,
        });
        showToast("Interaction ajoutée avec succès", "success");
      }

      await fetchInteractions();
      await fetchNextSteps();
      setShowForm(false);
      setEditingInteractionId(null);
      setFormData({
        typeInteraction: "",
        canal: "",
        date: "",
        contenu: "",
        prochaineEtape: "",
        dateProchaineInteraction: "",
        important: false,
      });
    } catch (err) {
      console.error("Erreur ajout/modification interaction :", err);
      showToast("Erreur lors de l'enregistrement", "error");
    }
  };

  const handleEditClick = (item) => {
    setEditingInteractionId(item.id || item._id);
    setFormData({
      typeInteraction: item.typeInteraction || "",
      canal: item.canal || "",
      date: item.date ? new Date(item.date).toISOString().split('T')[0] : "",
      contenu: item.contenu || "",
      prochaineEtape: item.prochaineEtape || "",
      dateProchaineInteraction: item.dateProchaineInteraction ? new Date(item.dateProchaineInteraction).toISOString().split('T')[0] : "",
      important: item.important || false,
    });
    setShowForm(true);
  };

  const handleAddClick = () => {
    setEditingInteractionId(null);
    setFormData({
      typeInteraction: "",
      canal: "",
      date: new Date().toISOString().split('T')[0],
      contenu: "",
      prochaineEtape: "",
      dateProchaineInteraction: "",
      important: false,
    });
    setShowForm(true);
  };

  const { confirm } = useModal();


  const handleDelete = async (interactionId) => {
    const isConfirmed = await confirm({
      title: "Supprimer l'interaction",
      message: "Voulez-vous vraiment supprimer cette interaction ?",
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler"
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/interactions/${interactionId}`);
      await fetchInteractions();
      await fetchNextSteps();
      showToast("Interaction supprimée", "success");
    } catch (err) {
      console.error("Erreur suppression :", err);
      showToast("Erreur lors de la suppression", "error");
    }
  };

  const handleMarkAsDone = async (action) => {
    try {
      await api.post("/interactions", {
        typeInteraction: action.type,
        canal: action.canal === "N/A" ? "Autre" : action.canal,
        date: new Date(),
        contenu: action.titre,
        statut: prospect.status || prospect.statut,
        referent: prospect.ownerMembership?.user?.displayName || prospect.ownerMembershipId || prospect.referent || "Non assigné",
        prospectId: prospect._id || prospect.id,
        commentaireInterne: "Interaction ajoutée depuis la section 'À Faire'.",
      });

      if (
        (prospect.status === "NEW" || prospect.statut === "Prospect" || prospect.status === "PENDING") &&
        action.titre === "Faire signer la Lettre d’Entrée en Relation"
      ) {
        await api.put(`/prospects/${prospect._id || prospect.id}`, { status: "QUALIFIED" });
        await fetchProspect();
        await fetchInteractions();
        await fetchNextSteps();
        showToast("Statut mis à jour vers 'Prospect Qualifié' !", "success");
      } else {
        await fetchInteractions();
        await fetchNextSteps();
        showToast("Interaction enregistrée !", "success");
      }

      if (
        (prospect.status === "QUALIFIED" || prospect.statut === "Prospect Qualifié") &&
        action.titre === "Fixer un rendez-vous pour l'audit patrimonial"
      ) {
        setShowAuditButton(true);
      }
    } catch (err) {
      console.error("Erreur enregistrement interaction :", err);
      showToast("Erreur d'enregistrement", "error");
    }
  };

  const handleSaveNotes = async () => {
    try {
      await api.put(`/prospects/${prospect._id || prospect.id}`, { interestNotes: notesValue });
      setProspect({ ...prospect, interestNotes: notesValue });
      setIsEditingNotes(false);
      showToast("Notes mises à jour", "success");
    } catch (err) {
      console.error("Erreur mise à jour notes :", err);
      showToast("Erreur lors de la mise à jour des notes", "error");
    }
  };

  const handleConfirmAudit = async () => {
    try {
      const res = await api.post(`/prospects/transformer/${prospect.id || prospect._id}`, {});

      if (res.data?.redirection === "societes") {
        navigate(`/societes/detail/${res.data.id}`);
      } else {
        navigate(`/familles/detail/${res.data.id}`);
      }

    } catch (error) {
      console.error("Erreur transformation en client :", error);
      showToast("Erreur : " + (error.response?.data?.message || "Erreur inconnue"), "error");
    }
  };

  async function fetchNextSteps() {
    try {
      const res = await api.get(`/interactions/${prospect._id || prospect.id}`);
      const allInteractions = res.data;
      
      const interactionsFuturesFaites = new Set(
        allInteractions.map(i => i.contenu?.trim())
      );

      const futures = allInteractions
        .filter(i => (i.prochaineEtape || i.dateProchaineInteraction) && !interactionsFuturesFaites.has(i.prochaineEtape?.trim()))
        .sort((a, b) => new Date(a.dateProchaineInteraction) - new Date(b.dateProchaineInteraction));

      const iconByType = {
        Appel: <Phone className="w-5 h-5 text-secondary" />,
        Email: <Mail className="w-5 h-5 text-warning-500" />,
        "Rendez-vous": <Calendar className="w-5 h-5 text-success-500" />,
        "Message LinkedIn": <Linkedin className="w-5 h-5 text-primary-500" />,
        "Note interne": <ClipboardList className="w-5 h-5 text-subtle" />,
        Autre: <User className="w-5 h-5 text-subtle" />,
      };

      const suggestionsParStatut = {
        NEW: [
          { type: "Appel", note: "Premier échange téléphonique" },
          { type: "Email", note: "Envoyer brochure de présentation" },
          { type: "Message LinkedIn", note: "Ajouter sur LinkedIn" },
          { type: "Rendez-vous", note: "Proposer un rendez-vous" },
          { type: "Note interne", note: "Préparer la Lettre d’Entrée en Relation" },
          { type: "Email", note: "Faire signer la Lettre d’Entrée en Relation" },
        ],
        PENDING: [
          { type: "Appel", note: "Premier échange téléphonique" },
          { type: "Email", note: "Envoyer brochure de présentation" },
          { type: "Message LinkedIn", note: "Ajouter sur LinkedIn" },
          { type: "Rendez-vous", note: "Proposer un rendez-vous" },
          { type: "Note interne", note: "Préparer la Lettre d’Entrée en Relation" },
          { type: "Email", note: "Faire signer la Lettre d’Entrée en Relation" },
        ],
        QUALIFIED: [
          { type: "Email", note: "Envoyer le questionnaire patrimonial" },
          { type: "Note interne", note: "Collecter documents financiers et administratifs" },
          { type: "Rendez-vous", note: "Fixer un rendez-vous pour l'audit patrimonial" },
        ],
        CONVERTED: [
          { type: "Note interne", note: "Lancer l’analyse du patrimoine" },
          { type: "Email", note: "Envoyer proposition d’allocation" },
          { type: "Appel", note: "Relancer pour retour sur proposition" },
        ],
        DISQUALIFIED: [
          { type: "Note interne", note: "Clore le dossier (sans suite)" },
        ],
      };

      const suggestionsDéjàRéalisées = new Set(
        allInteractions.map(i => `${i.typeInteraction}_${i.contenu?.trim()}`)
      );

      const suggestionsFiltrées = (suggestionsParStatut[prospect?.status || prospect?.statut || "NEW"] || []).filter(
        sugg => !suggestionsDéjàRéalisées.has(`${sugg.type}_${sugg.note.trim()}`)
      );

      const actionsFutures = futures.map(item => ({
        icon: iconByType[item.typeInteraction] || <FileText className="w-5 h-5 text-subtle" />,
        titre: item.prochaineEtape || "Étape à préciser",
        date: item.dateProchaineInteraction ? new Date(item.dateProchaineInteraction).toLocaleDateString() : null,
        canal: item.canal,
        type: item.typeInteraction,
      }));

      const actionsSuggestions = suggestionsFiltrées.map(sugg => ({
        icon: iconByType[sugg.type] || <FileText className="w-5 h-5 text-subtle" />,
        titre: sugg.note,
        type: sugg.type,
        canal: "N/A",
        date: null,
      }));

      setProchainesActions([...actionsFutures, ...actionsSuggestions]);
    } catch (err) {
      console.error("Erreur récupération prochaines étapes :", err);
      setProchainesActions([]);
    }
  }

  const getFullAddress = () => {
    const parts = [
      prospect.address,
      prospect.postalCode,
      prospect.city,
      prospect.country
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "Non renseignée";
  };

  if (loading) return <MiniLoader />;
  if (errorStatus === 401 || errorStatus === 403) return <Unauthorized />;
  if (!prospect || errorStatus === 404) return <NotFound />;

  const isPM = prospect.type === "Personne Morale";
  const initials = isPM 
    ? prospect.organizationName?.[0]?.toUpperCase() || ""
    : `${prospect.firstName?.[0] || ""}${prospect.lastName?.[0] || ""}`.toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={isLoading ? { opacity: 0, y: 10 } : { opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="p-4 sm:p-8 space-y-6"
    >
      <div className="flex justify-between items-center px-2">
        <Breadcrumbs 
          items={[
            { label: 'Prospects', path: '/prospects/prospects' },
            { label: isPM ? prospect.organizationName : `${prospect.firstName || ""} ${prospect.lastName || ""}`.trim() }
          ]} 
        />
        <button
          className="btn btn-primary"
          onClick={() => setOpenEditModal(true)}
        >
          <Pencil className="w-4 h-4 mr-2" />
          Modifier
        </button>
      </div>

      <div className="workspace-card bg-surface shadow-hp-sm rounded-xl p-6 h-auto relative overflow-hidden">
        {/* 👤 Profil + Progression */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center h-[160px]">
          {/* 🟦 Bloc à diviser en 2 colonnes */}
          <div className="grid grid-cols-3 items-center w-full">
            {/* 🟠 Initiales + nom/statut (2/3) */}
            <div className="col-span-2 flex items-center space-x-6">
              <div className="w-[120px] h-[120px] flex items-center justify-center rounded-full bg-surface-muted text-5xl font-bold text-muted border border-workspace shadow-sm">
                {initials}
              </div>
              <div>
                <h2 className="text-4xl font-semibold text-primary">
                  {isPM ? prospect.organizationName : `${prospect.firstName || ""} ${prospect.lastName || ""}`.trim()}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                    prospect.status === "CONVERTED" ? "bg-success-50 text-success-700 border-success-200" :
                    prospect.status === "DISQUALIFIED" ? "bg-danger-50 text-danger-700 border-danger-200" :
                    prospect.status === "QUALIFIED" ? "bg-primary-50 text-primary-700 border-primary-200" :
                    "bg-warning-50 text-warning-700 border-warning-200"
                  }`}>
                    {prospect.status === "CONVERTED" && <CheckCircle2 className="w-4 h-4 text-success-500" />}
                    {prospect.status === "DISQUALIFIED" && <XCircle className="w-4 h-4 text-danger-500" />}
                    {prospect.status === "QUALIFIED" && <User className="w-4 h-4 text-primary-500" />}
                    {(prospect.status === "NEW" || prospect.status === "PENDING" || !prospect.status) && <AlertTriangle className="w-4 h-4 text-warning-500" />}
                    {prospect.status}
                  </span>
                </div>
              </div>
            </div>

            {/* 📅 Date ajoutée (1/3) */}
            <div className="col-span-1 flex flex-col items-start justify-center pl-20 text-muted border-l border-workspace">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-secondary" />
                <span className="text-lg font-medium">Ajouté le :</span>
              </div>
              <span className="text-md text-muted ml-7">{new Date(prospect.createdAt).toLocaleDateString()}</span>
            </div>
          </div>

          {/* ✅ Progress Bar améliorée */}
          <div className="flex flex-col w-full">
            <div className="relative flex justify-between items-center w-full">
              {["Ajout du Prospect", "Lettre d’Entrée en Relation", "Audit Patrimonial"].map((step, index, arr) => {
                const isCompleted =
                  ((prospect.status === "NEW" || prospect.statut === "Prospect") && index === 0) ||
                  ((prospect.status === "QUALIFIED" || prospect.statut === "Prospect Qualifié") && index <= 1) ||
                  (prospect.status === "CONVERTED" || prospect.statut === "Client");

                const isCurrent =
                  ((prospect.status === "NEW" || prospect.statut === "Prospect") && index === 0) ||
                  ((prospect.status === "QUALIFIED" || prospect.statut === "Prospect Qualifié") && index === 1) ||
                  ((prospect.status === "CONVERTED" || prospect.statut === "Client") && index === 2);

                return (
                  <div key={index} className="relative flex-1 flex flex-col items-center">
                    {index !== 0 && (
                      <div className={`absolute top-4 right-1/2 w-full h-1 z-0 
                        ${isCompleted ? "bg-primary" : "bg-surface-muted"} 
                      `}></div>
                    )}

                    {/* Point */}
                    <div className={`relative z-10 w-9 h-9 flex items-center justify-center rounded-full border-2 transition-colors
                      ${isCurrent ? "bg-surface border-primary text-secondary" :
                        isCompleted ? "bg-primary border-primary text-white" :
                          "bg-surface border-tertiary text-subtle"}
                    `}>
                      {isCompleted ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <span className="text-sm font-semibold">{index + 1}</span>
                      )}
                    </div>

                    {/* Label */}
                    <span className="mt-2 text-xs text-center text-muted w-[90px]">{step}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-10 text-sm text-muted">
          {/* 🧾 Infos prospect */}
          <div className="md:col-span-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                icon: <User className="w-5 h-5 text-secondary" />,
                label: isPM ? "Contact" : "Nom",
                value: `${prospect.firstName || ""} ${prospect.lastName || ""}`.trim() || "Non renseigné",
              },
              {
                icon: <Phone className="w-5 h-5 text-success-600" />,
                label: "Téléphone",
                value: prospect.phone || "Non renseigné",
              },
              {
                icon: <Mail className="w-5 h-5 text-warning-600" />,
                label: "Email",
                value: prospect.email || "Non renseigné",
              },
              {
                icon: <Briefcase className="w-5 h-5 text-muted" />,
                label: "Profession",
                value: prospect.position || "Non renseignée",
              },
              {
                icon: <Euro className="w-5 h-5 text-info-600" />,
                label: "Enveloppe",
                value: prospect.estimatedWealthBracket || "Non renseignée",
              },
              !isPM && {
                icon: <User className="w-5 h-5 text-teal-600" />,
                label: "Situation Matr.",
                value: prospect.maritalStatus || "Non renseignée",
              },
              {
                icon: <UserCircle className="w-5 h-5 text-pink-600" />,
                label: "Référent",
                value: prospect.ownerMembership?.user?.displayName || prospect.referent || "Non assigné",
              },
              prospect.linkedinUrl && {
                icon: <Linkedin className="w-5 h-5 text-info-600" />,
                label: "LinkedIn",
                value: (
                  <a href={prospect.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-info-600 hover:underline">
                    Voir le profil
                  </a>
                ),
              },
              prospect.source && {
                icon: <Globe className="w-5 h-5 text-teal-600" />,
                label: "Source",
                value: prospect.source,
              },
              prospect.createdAt && {
                icon: <Calendar className="w-5 h-5 text-purple-500" />,
                label: "1er Contact",
                value: new Date(prospect.createdAt).toLocaleDateString(),
              },
            ]
              .filter(Boolean)
              .map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 bg-workspace border border-workspace rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
                >
                  {item.icon}
                  <div>
                    <p className="text-sm font-semibold text-muted">{item.label}</p>
                    <p className="text-sm text-primary font-medium">{item.value}</p>
                  </div>
                </div>
              ))}

            {/* Adresse */}
            <div className="md:col-span-2 bg-workspace border border-workspace rounded-lg p-3 shadow-sm flex items-start gap-3">
              <MapPin className="w-5 h-5 text-orange-500 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-muted">Adresse</p>
                <p className="text-sm text-primary font-medium">{getFullAddress()}</p>
              </div>
            </div>

            {/* Besoins */}
            <div className="md:col-span-2 bg-workspace border border-workspace rounded-lg p-3 shadow-sm flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-indigo-500 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-muted">Besoins</p>
                <p className="text-sm text-primary font-medium whitespace-pre-wrap">{prospect.needs || "Non renseignés"}</p>
              </div>
            </div>

            {/* Infos Personne Morale */}
            {isPM && (
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="flex items-start gap-3 bg-workspace border border-workspace rounded-lg p-3 shadow-sm">
                  <Building2 className="w-5 h-5 text-teal-600" />
                  <div>
                    <p className="text-sm font-semibold text-muted">Statut Juridique</p>
                    <p className="text-sm text-primary font-medium">{prospect.legalStatus}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-workspace border border-workspace rounded-lg p-3 shadow-sm">
                  <Building2 className="w-5 h-5 text-teal-600" />
                  <div>
                    <p className="text-sm font-semibold text-muted">SIRET</p>
                    <p className="text-sm text-primary font-medium">{prospect.siret}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-workspace border border-workspace rounded-lg p-3 shadow-sm">
                  <Building2 className="w-5 h-5 text-teal-600" />
                  <div>
                    <p className="text-sm font-semibold text-muted">Secteur</p>
                    <p className="text-sm text-primary font-medium">{prospect.sector}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Notes d'équipe */}
            <div className="md:col-span-2 bg-workspace border border-workspace rounded-lg p-3 shadow-sm">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-semibold text-muted flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Notes d'équipe
                </p>
                {!isEditingNotes && (
                  <button onClick={() => setIsEditingNotes(true)} className="text-secondary hover:text-secondary-600 text-xs font-medium">
                    Modifier
                  </button>
                )}
              </div>
              {isEditingNotes ? (
                <div className="flex flex-col gap-2">
                  <textarea
                    value={notesValue}
                    onChange={(e) => setNotesValue(e.target.value)}
                    className="workspace-input text-sm w-full"
                    rows={4}
                    placeholder="Ajoutez vos notes internes ici..."
                  />
                  <div className="flex justify-end gap-2 mt-1">
                    <button onClick={() => setIsEditingNotes(false)} className="btn btn-secondary btn-sm text-xs py-1 px-2">Annuler</button>
                    <button onClick={handleSaveNotes} className="btn btn-primary btn-sm text-xs py-1 px-2">Enregistrer</button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-primary whitespace-pre-wrap">
                  {prospect.interestNotes || <span className="text-subtle italic">Aucune note</span>}
                </div>
              )}
            </div>
          </div>

          {/* 💬 Interactions + À Faire (6 colonnes sur 12) */}
          <div className="md:col-span-6 space-y-4">
            {/* 💬 Interactions */}
            <div className="bg-workspace border border-workspace rounded-lg p-4 h-fit min-h-[270px]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-secondary" /> Interactions
                </h3>
                <button
                  onClick={handleAddClick}
                  className="btn btn-primary btn-sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter
                </button>
              </div>

              {interactions.length > 0 ? (
                <div className="space-y-3 pr-2 max-h-[300px] overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                  {interactions.map((item) => (
                    <div key={item.id || item._id} className="group relative border-l-4 border-primary-500 pl-4 pr-10 py-3 bg-surface rounded-md shadow-sm z-10 hover:z-40 mr-1">
                      <div className="flex justify-between items-start">
                        <p className="text-sm text-primary font-semibold">
                          {item.typeInteraction} via {item.canal} – {new Date(item.date).toLocaleDateString()}
                        </p>
                      </div>
                      <p className="text-sm text-muted mt-1">{item.contenu}</p>

                      {item.prochaineEtape && (
                        <p className="text-sm text-secondary font-medium mt-2 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {item.prochaineEtape}{" "}
                          {item.dateProchaineInteraction &&
                            `(le ${new Date(item.dateProchaineInteraction).toLocaleDateString()})`}
                        </p>
                      )}

                      <div className="absolute bottom-2 right-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Tooltip content="Modifier" position="top">
                          <button
                            onClick={() => handleEditClick(item)}
                            className="text-primary-600 hover:text-primary-800 transition-colors p-1"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Supprimer" position="top">
                          <button
                            onClick={() => handleDelete(item.id || item._id)}
                            className="text-danger-600 hover:text-danger-800 transition-colors p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>

                      {item.important && (
                        <div className="absolute top-2 right-2 text-warning-500">
                          <Tooltip content="Important">
                            <AlertTriangle className="w-5 h-5" />
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-subtle text-center py-4">Aucune interaction enregistrée.</p>
              )}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {/* Box Score Ollama */}
              <div className="bg-surface border border-workspace rounded-lg p-4 h-fit flex flex-col">
                <h3 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-danger-500" />
                  Score IA
                </h3>
                <p className="text-xs text-subtle italic mb-6">
                  Ce score est calculé par Ollama en fonction des informations et de la qualité du profil présenté.
                </p>
                <div className="flex-1 flex items-center justify-center">
                  <div className={`text-5xl font-bold ${
                    (prospect.score ?? 0) >= 70 ? 'text-success-600' :
                    (prospect.score ?? 0) >= 40 ? 'text-warning-500' : 'text-danger-500'
                  }`}>
                    {prospect.score !== undefined && prospect.score !== null ? `${prospect.score}/100` : "N/A"}
                  </div>
                </div>
              </div>

              {/* À Faire */}
              <div className="bg-surface border border-workspace rounded-lg p-4 h-fit flex flex-col">
                <h3 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-primary" /> À Faire
                </h3>

                {prochainesActions.length > 0 ? (
                  <ul className="space-y-3 flex-1">
                    {prochainesActions.slice(0, 2).map((action, idx) => (
                      <li
                        key={idx}
                        className="bg-workspace border border-workspace rounded-lg p-3 flex flex-col gap-2"
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-1">{action.icon}</div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-primary text-sm truncate">
                              {action.titre}{action.date && ` – ${action.date}`}
                            </p>
                            <p className="text-xs text-subtle italic">
                              ({action.type}{action.canal !== "N/A" && ` via ${action.canal}`})
                            </p>
                          </div>
                        </div>

                        <button
                          className="flex items-center justify-center gap-2 w-full mt-2 py-1.5 text-xs font-bold text-success-600 bg-success-50 rounded-md border border-success-100 hover:bg-success-100 transition-colors"
                          onClick={() => handleMarkAsDone(action)}
                        >
                          <Check className="w-4 h-4" />
                          Marquer comme faite
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-subtle text-center py-4 flex-1 flex items-center justify-center">Aucune tâche prévue.</p>
                )}

                {showAuditButton && (
                  <div className="mt-4 flex justify-center border-t border-workspace pt-4">
                    <button
                      className="btn btn-primary bg-info-600 hover:bg-info-700 text-white w-full max-w-xs"
                      onClick={() => setShowConfirmModal(true)}
                    >
                      Audit Patrimonial
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProspectForm
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        onSave={async (updatedData) => {
          try {
            const res = await api.put(
              `/prospects/${updatedData.id || updatedData._id}`,
              updatedData
            );
            setProspect(res.data);
            setOpenEditModal(false);
          } catch (error) {
            console.error("Erreur lors de la mise à jour du prospect :", error);
          }
        }}
        prospect={prospect}
        referent={prospect.referent}
      />

      {/* 💬 Modales Animées */}
      <AnimatePresence>
        {showConfirmModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-surface p-6 rounded-xl shadow-xl w-[380px]"
            >
              <h3 className="text-lg font-semibold mb-4 text-primary">Confirmer la transformation en client ?</h3>
              <p className="text-sm text-muted mb-6">
                Cette action transférera les données dans les familles, mettra à jour le statut et supprimera le prospect.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-primary bg-success-600 hover:bg-success-700"
                  onClick={handleConfirmAudit}
                >
                  Confirmer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {showFinalModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-surface p-6 rounded-xl shadow-xl w-[380px] text-center"
            >
              <h3 className="text-lg font-semibold mb-4 text-success-600 flex items-center justify-center gap-2">
                <CheckCircle2 className="w-6 h-6" /> Transformation réussie
              </h3>
              <p className="text-sm text-muted mb-6">
                Le prospect a été converti en client. Vous pouvez désormais consulter sa fiche famille.
              </p>
              <button
                className="btn btn-primary w-full justify-center"
                onClick={() => window.location.href = "/prospects/familles"}
              >
                Aller à la fiche famille
              </button>
            </motion.div>
          </motion.div>
        )}


        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-surface rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4"
            >
              <h3 className="text-lg font-semibold text-primary">
                {editingInteractionId ? "Modifier l'interaction" : "Nouvelle interaction"}
              </h3>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Type d'interaction *</label>
                  <CustomSelect
                    value={formData.typeInteraction}
                    onChange={(val) => handleChange({ target: { name: 'typeInteraction', value: val } })}
                    placeholder="-- Sélectionner --"
                    options={[
                      { label: "Appel", value: "Appel" },
                      { label: "Email", value: "Email" },
                      { label: "Rendez-vous", value: "Rendez-vous" },
                      { label: "Message LinkedIn", value: "Message LinkedIn" },
                      { label: "Note interne", value: "Note interne" },
                      { label: "Autre", value: "Autre" }
                    ]}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Canal *</label>
                  <CustomSelect
                    value={formData.canal}
                    onChange={(val) => handleChange({ target: { name: 'canal', value: val } })}
                    placeholder="-- Sélectionner --"
                    options={[
                      { label: "Téléphone", value: "Téléphone" },
                      { label: "Email", value: "Email" },
                      { label: "LinkedIn", value: "LinkedIn" },
                      { label: "Visio", value: "Visio" },
                      { label: "WhatsApp", value: "WhatsApp" },
                      { label: "Autre", value: "Autre" }
                    ]}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Date *</label>
                  <Input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Contenu *</label>
                  <textarea
                    name="contenu"
                    value={formData.contenu}
                    onChange={handleChange}
                    rows={3}
                    className="workspace-input mt-1"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Prochaine étape</label>
                  <Input
                    name="prochaineEtape"
                    value={formData.prochaineEtape}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Date prochaine interaction</label>
                  <Input
                    type="date"
                    name="dateProchaineInteraction"
                    value={formData.dateProchaineInteraction}
                    onChange={handleChange}
                  />
                </div>

                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    name="important"
                    checked={formData.important}
                    onChange={handleChange}
                    className="h-4 w-4 text-primary rounded border-workspace focus:ring-primary-500"
                  />
                  <label className="text-sm text-primary">Marquer comme important</label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-workspace">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowForm(false)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveInteraction}
                >
                  Enregistrer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

      </AnimatePresence>
    </motion.div>
  );
}
